"use client";
import Pagination from "@/components/pagination/pagination";
import { SortDropDown } from "@/components/sortDropdown/sortDropdown";
import { Items } from "@/components/items/items";
import { useTranslations } from "next-intl";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useEffect, useRef } from "react";
import { AppEventEmitter } from "@/components/appEventEmitter/appEventEmitter";
import useSWR from "swr";
import { getRetailAllItemsAction } from "../../[slug]/actions";
import { Layout } from "@/dto/item";

interface AllContainerProps {
  merchantId: string;
  searchParams?: { [key: string]: string | string[] | undefined };
  currentPage: number;
  totalPages: number;
  items: any[];
  pageSize: string;
  itemTemplate?: string;
  isSimpleStorefront: boolean;
  totalItemCount: number;
  searchQuery?: string;
  merchantName: string;
  layout?: Layout;
}

export const AllContainer = ({
  merchantId,
  searchParams,
  currentPage,
  totalPages,
  items,
  pageSize,
  itemTemplate = "Image Focused",
  isSimpleStorefront,
  totalItemCount,
  searchQuery,
  merchantName,
  layout = "default",
}: AllContainerProps) => {
  const t = useTranslations("allContainer");
  const headerRef = useRef<HTMLHeadingElement>(null);

  const { data: swrData } = useSWR(
    merchantId
      ? ["retailAllItems", merchantId, JSON.stringify(searchParams ?? {})]
      : null,
    () => getRetailAllItemsAction(merchantId, searchParams ?? {}),
    {
      fallbackData: {
        data: {
          elements: items,
          currentPage,
          totalPages,
          totalItemCount,
        },
      },
      revalidateOnMount: false,
      revalidateOnFocus: true,
    }
  );

  const activeItems = swrData?.data?.elements ?? items;
  const activeCurrentPage = swrData?.data?.currentPage ?? currentPage;
  const activeTotalPages = swrData?.data?.totalPages ?? totalPages;
  const activeTotalItemCount = swrData?.data?.totalItemCount ?? totalItemCount;

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.focus();
    }
  }, [searchQuery]);

  return (
    <div className="flex-1 flex flex-col px-6 sm:px-12 py-8">
      <div
        className="flex flex-col sm:flex-row w-full justify-between gap-4 mb-4"
        data-testid={applyDataTestId(pom.retailCategories.categoryHeader)}
      >
        <AppEventEmitter />

        <h1
          ref={headerRef}
          tabIndex={-1}
          className="font-semibold text-[30px] leading-[45px] outline-none"
        >
          {searchQuery == undefined
            ? t("allItems")
            : `${t("youSearchedFor")} "${searchQuery}"`}
        </h1>
        {activeItems.length > 0 && <SortDropDown />}
      </div>
      {searchQuery != undefined && !!activeTotalItemCount && (
        <div className="w-full text-[20px] text-[#4B5563] pt-4 sm:pt-1">
          {activeTotalItemCount}{" "}
          {activeTotalItemCount > 1 ? t("results") : t("result")} {t("found")}.
        </div>
      )}
      {activeItems.length > 0 ? (
        <>
          <Items
            itemTemplate={itemTemplate}
            items={activeItems}
            merchantName={merchantName}
            layout={layout}
          />
          <Pagination
            currentPage={activeCurrentPage}
            totalPages={activeTotalPages}
            pageSize={pageSize}
            itemCount={activeItems.length}
            totalItemCount={activeTotalItemCount}
          />
        </>
      ) : (
        <p
          className="text-[20px] text-[#4B5563] font-normal"
          data-testid={applyDataTestId(pom.searchPage.emptyResults)}
        >
          {t("noResult")} {t("found")}.
        </p>
      )}
    </div>
  );
};
